import { FC, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, CircularProgress, Stack } from '@mui/material';
import { Check } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { LinkAsButton, Typography } from 'melp-design/components';
import { useJobsContext, useJobStatus } from 'store/jobs';
import { surveysKeys } from 'store/surveys';
import { SystemColors } from 'melp-design/style';
import { formatPercents } from 'utils/format';
import { ROUTES } from 'config';
import { routerHistory } from 'utils/router-history';

const PROGRESS_SIZE = 32;
const SUCCESS_AUTO_CLOSE_IN = 1000 * 20;
const JOB_STATUS_REFETCH_INTERVAL = 3000;

interface Props {
  jobId: string;
  toastId: string;
}

const TrainingGenerationProgressToast: FC<Props> = ({ jobId, toastId }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { removeInProgressJob } = useJobsContext();

  const { data: job } = useJobStatus(
    { queue: 'aiTrainingQueue', jobId },
    { refetchInterval: JOB_STATUS_REFETCH_INTERVAL },
  );

  useEffect(() => {
    if (job?.state === 'success') {
      removeInProgressJob(jobId);
      queryClient.invalidateQueries(surveysKeys.lists);

      const timeoutId = setTimeout(() => {
        toast.dismiss(toastId);
      }, SUCCESS_AUTO_CLOSE_IN);

      return () => clearTimeout(timeoutId);
    }

    if (job?.state === 'error') {
      removeInProgressJob(jobId);
      toast.dismiss(toastId);
      toast.error(job.error || t('errors.genericErrorMessage'));
    }
  }, [job, jobId, queryClient, removeInProgressJob, t, toastId]);

  if (!job) return null;

  const isSuccess = job.state === 'success';
  const surveyId = job.output?.surveyId;

  return (
    <Stack direction="row" alignItems="center" gap={2}>
      <Box
        position="relative"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        width={PROGRESS_SIZE}
        height={PROGRESS_SIZE}
        borderRadius="50%"
        sx={{ backgroundColor: SystemColors.primary.tint }}
        flexShrink={0}
      >
        {isSuccess ? (
          <Check sx={{ color: SystemColors.primary.base, fontSize: 20 }} />
        ) : (
          <>
            <CircularProgress
              size={PROGRESS_SIZE}
              value={job.percent}
              variant="determinate"
            />
            <Box
              position="absolute"
              top={0}
              left={0}
              bottom={0}
              right={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography variant="p3" color="primary">
                {formatPercents(i18n.language, job.percent / 100)}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <Stack gap={0.5} flex={1} minWidth={0}>
        <Typography variant="h3">
          {isSuccess
            ? t('common.added_successfully')
            : t('trainings.generate.in_progress.title')}
        </Typography>

        {!isSuccess ? (
          <Typography variant="p1" color="textSecondary">
            {job.currentTask}
          </Typography>
        ) : null}
      </Stack>

      {isSuccess && surveyId ? (
        <LinkAsButton
          label={t('common.open')}
          color="primary"
          onClick={() => {
            routerHistory.push(
              ROUTES.surveys.training.details.replace(':id', surveyId),
            );
            toast.dismiss(toastId);
          }}
        />
      ) : null}
    </Stack>
  );
};

export const openTrainingGenerationProgressToast = (jobId: string) => {
  const toastId = `training-generation-progress-${jobId}`;

  toast(<TrainingGenerationProgressToast jobId={jobId} toastId={toastId} />, {
    toastId,
    position: 'bottom-right',
    autoClose: false,
    closeOnClick: false,
    closeButton: false,
    draggable: false,
    style: { cursor: 'default' },
  });
};
